import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Form, Button, Card, Tab, Tabs, Spinner } from 'react-bootstrap';
import { useOutletContext, useParams, useNavigate } from 'react-router-dom';
//import spinnerIcon from './/./../assets/img/loadingSpinner.gif'
import SpinnerComponent from '../components/progressbar/SpinnerComponent.js'
import PaginationComponent from '../components/pagination/PaginationComponent.js'
import { useAuth0 } from "@auth0/auth0-react";
import { apiGet, apiPut } from "../services/AuthService.js";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPenToSquare } from '@fortawesome/free-solid-svg-icons'
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons'
import {
    btnGold, boxshadowHover, boxshadowUnHover,
    formLabel, inputSm, goldInputFocus,
    paginationStyle, activePaginationStyle, COLORS, backBtn
} from '../styles/goldTheme.js'

//The Global Mapping account ID (the Account row with IsGlobalMapping = true) varies
//per environment, so we fetch it from the backend at runtime rather than hardcoding.
//The value lives in state and is passed through to populate on first load so the
//initial render doesn't race the async fetch.

const ClassificationCodesOverride_dc = props => {

    //properties
    const baseUrl = process.env.REACT_APP_API_BASEURL;
    const { getAccessTokenSilently } = useAuth0();

    //stateful properties (hooks)
    const params = useParams();
    const [currentAccount, accountProfile, setAccountProfile, toggleIsShowSidebarMd, setIsShowSidebarMd] = useOutletContext();
    const navigate = useNavigate();

    //owner override view; always uses the signed-in account.
    //Rows start as read-only — clicking the pencil icon enables editing
    //(and persists as an override of the global mapping on save).
    const targetAccountId = currentAccount && currentAccount.id;
    const [pageStatus, setPageStatus] = useState('loading');
    const [dataStatus, setDataStatus] = useState('loading');
    const [pageErrors, setPageErrors] = useState('');
    const [dataErrors, setDataErrors] = useState('');
    const [classificationTypes, setClassificationTypes] = useState([{ code: '', value: '' }, { code: 'CAPITAL', value: 'CAPITAL' }, { code: 'EXPENSE', value: 'EXPENSE' }, { code: 'NET', value: 'NET' }, { code: 'CASH_CALL', value: 'CASH_CALL' }]);
    const [originalClassificationCodes, setOriginalClassificationCodes] = useState([]);
    const [classificationCodes, setClassificationCodes] = useState([]);
    const [selectedClassificationIndex, setSelectedClassificationIndex] = useState(null);
    const [selectedClassification, setSelectedClassification] = useState(null);
    const [anyClassificationDirty, setAnyClassificationDirty] = useState(false);
    //const [anyTransactionDirty, setAnyTransactionDirty] = useState(false);
    const [isReadOnly, setIsReadOnly] = useState(true);
    const [hoveredIndex, setHoveredIndex] = useState(null);
    //the Account row flagged IsGlobalMapping = true. Loaded from the backend on mount.
    const [globalAccountId, setGlobalAccountId] = useState(null);
    const [dataCriteria, setDataCriteria] = useState({
        skipCount: 0,
        takeCount: 25
    });
    const [rowCount, setRowCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const companyTopRef = useRef(null);
    const editRef = useRef(null);

    //initialize page — fetch the global mapping account id first, then populate
    useEffect(() => {
        (async () => {
            if (!props.embedded && setIsShowSidebarMd)
                setIsShowSidebarMd(true);

            const init = await initializeForm();
            if (!init.success) {
                setPageStatus('error');
                setPageErrors(init.error || 'Failed to initialize page.');
                return;
            }
            try {
                //pass the freshly-fetched id directly so we don't race React's state update
                await populateMajorAccountCodes(1, init.globalAccountId);
                setPageStatus('ready');
            } catch (ex) {
                setPageStatus('error');
                setPageErrors(ex.message);
                setDataStatus('error');
                setDataErrors(ex.message);
            }
        })();
    }, []);

    //initialize form — fetch the global mapping account id from the backend
    const initializeForm = async () => {
        try {
            const response = await apiGet('accounts/GlobalMapping/Id', getAccessTokenSilently);
            if (!response.ok) {
                return { success: false, error: 'Failed to fetch global mapping account ID.' };
            }
            const id = await response.json();
            if (!id) {
                return { success: false, error: 'No global mapping account configured for this environment.' };
            }
            setGlobalAccountId(id);
            return { success: true, globalAccountId: id };
        } catch (ex) {
            return { success: false, error: ex.message || 'Error fetching global mapping account ID.' };
        }
    };

    //populate majorAccountCodes
    //
    //Override page merges two API responses:
    //  - Global mappings from the TOTData Global Mapping account (master list)
    //  - Current account's existing overrides
    //
    //For each global row:
    //  - if current account has a row with a matching majorAccountDesc, that's a
    //    pre-existing override: use the current account's majorClassCode value, mark
    //    the row as editable (isReadOnly = false), no pencil
    //  - if no match, inherit global values: mark read-only with pencil so user can
    //    click to start overriding
    //explicitGlobalId (optional): pass the global mapping account id directly to skip
    //the state read — needed on first load because the state hasn't propagated yet
    //after the just-completed initializeForm fetch. Subsequent calls (e.g. pagination)
    //fall back to the state value.
    const populateMajorAccountCodes = async (currentPage, explicitGlobalId) => {
        //note: have to recalcaulate skipCount here since state updates aren't active until re-render
        let skipCount = (currentPage === 1 ? 0 : (currentPage - 1) * dataCriteria.takeCount);

        const currentCriteria = {
            skipCount: skipCount,
            takeCount: dataCriteria.takeCount
        };

        const globalId = explicitGlobalId !== undefined ? explicitGlobalId : globalAccountId;
        if (!globalId) {
            setDataStatus('error');
            setDataErrors('Global mapping account ID is not available yet.');
            return;
        }

        try {
            //fire both calls in parallel
            const [globalResponse, currentResponse] = await Promise.all([
                getMajorAccountCodes(currentCriteria, globalId),
                getMajorAccountCodes(currentCriteria, targetAccountId)
            ]);

            if (!globalResponse.data || globalResponse.data.length === 0) {
                setHoveredIndex(null);
                setSelectedClassificationIndex(null);
                setSelectedClassification(null);
                setAnyClassificationDirty(false);
                setDataStatus('nodata');
                return;
            }

            //index current account's rows by lowercased majorAccountDesc
            const currentByDesc = new Map();
            (currentResponse.data || []).forEach(c => {
                if (c && c.majorAccountDesc) {
                    currentByDesc.set(c.majorAccountDesc.toLowerCase(), c);
                }
            });

            const mergedData = globalResponse.data.map(globalRow => {
                const override = globalRow.majorAccountDesc
                    ? currentByDesc.get(globalRow.majorAccountDesc.toLowerCase())
                    : null;

                if (override) {
                    //match → editable, no pencil; carry over the override's majorClassCode
                    return {
                        ...globalRow,
                        majorClassCode: override.majorClassCode,
                        isReadOnly: false
                    };
                }
                //no match → read-only with pencil
                return { ...globalRow, isReadOnly: true };
            });

            setIsReadOnly(true);
            setHoveredIndex(null);
            setSelectedClassificationIndex(0);
            setSelectedClassification(mergedData[0]);
            setAnyClassificationDirty(false);

            setRowCount(globalResponse.rowCount);
            setOriginalClassificationCodes(mergedData);
            setClassificationCodes(mergedData);
            setDataStatus('ready');
        } catch (ex) {
            setDataStatus('error');
            setDataErrors(ex.message);
        }
    };

    //get majorAccountCodes for a given accountId (used to load both the global mappings
    //and the current account's overrides in parallel)
    const getMajorAccountCodes = async (criteria, accountId) => {
        //console.log('Getting CdexProductCodes...');

        const apiUrl = 'accounts/' + accountId + '/majoraccountcodes?skipcount=' + criteria.skipCount + '&takecount=' + criteria.takeCount + '&documentType=DC_REV';
        //console.log(apiUrl);

        const response = await apiGet(apiUrl, getAccessTokenSilently);
        const json = await response.json();
        console.log('Response - Status: ' + response.status + ', Success: ' + response.ok + ', Json: ' + JSON.stringify(json));

        return { success: response.ok, rowCount: json.rowCount, data: json.data };
    };

    //handle product changes
    const handleClassificationChange = (event) => {
        event.preventDefault();

        const fieldName = event.target.name;
        let value = event.target.value;
        //console.log('ownerId: ' + ownerId, 'Field: ' + fieldName + ', Value: ' + value);

        let anyClassificationDirty = false;
        let anyClassificationInvalid = false;
        const newClassificationtList = [];
        classificationCodes.map((classification, index) => {
            if (index === selectedClassificationIndex) {
                const originalClassification = originalClassificationCodes[index];
                const originalValue = originalClassification.majorClassCode;

                const isDirty = (value !== originalValue ? true : false);
                const isValid = (value !== '' ? true : false);

                newClassificationtList.push({
                    ...classification,
                    [fieldName]: value,
                    isDirty: isDirty,
                    isValid: isValid
                });

                if (isDirty && isValid)
                    anyClassificationDirty = true;

                if (isDirty && !isValid)
                    anyClassificationInvalid = true;

                //console.log('Updating product: ' + product.productCode);
            }
            else {
                newClassificationtList.push(classification);

                if (classification.isDirty && classification.isValid)
                    anyClassificationDirty = true;

                if (classification.isDirty && !classification.isValid)
                    anyClassificationInvalid = true;
            }
        });
        setClassificationCodes(newClassificationtList);

        setAnyClassificationDirty(anyClassificationDirty && !anyClassificationInvalid);

        newClassificationtList.map(classification => {
            console.log('New Classification: ' + classification.majorAccountCode + ', ' + classification.majorAccountDesc + ', ' + classification.majorClassCode + ', ' + classification.isDirty);
        });
    }

    //handle transaction changes
    //const handleTransactionChange = (currentIndex, event) => {
    //    event.preventDefault();

    //    const fieldName = event.target.name;
    //    let value = event.target.value;
    //    //console.log('Current Index: ' + currentIndex + ', Field: ' + fieldName + ', Value: ' + value);

    //    if (fieldName == 'cdexTransactionCode') {
    //        if (value.length > 3) {
    //            value = value.substring(0, 3);
    //        }
    //    }

    //    let anyTransactionDirty = false;
    //    let anyTransactionInvalid = false;
    //    const newProductList = [];
    //    const newTransactionList = [];
    //    productCodes.map((product, productIndex) => {
    //        const originalProduct = originalProductCodes[productIndex];

    //        if (productIndex === selectedProductIndex) {

    //            product.cdexTransactionRowDTO.data.map((transaction, transactionIndex) => {

    //                if (transactionIndex === currentIndex) {
    //                    const originalTransaction = originalProduct.cdexTransactionRowDTO.data[transactionIndex];
    //                    const originalCdexTranCode = originalTransaction.cdexTransactionCode;
    //                    const originalRevLineTypeCode = originalTransaction.revLineTypeCode;

    //                    const cdexTranCodeValue = fieldName === 'cdexTransactionCode' ? value : transaction.cdexTransactionCode;
    //                    const revLineTypeCode = fieldName === 'revLineTypeCode' ? value : transaction.revLineTypeCode;
    //                    const isDirty = (cdexTranCodeValue !== originalCdexTranCode || revLineTypeCode !== originalRevLineTypeCode);
    //                    const isValid = (cdexTranCodeValue !== '' && revLineTypeCode !== '') || (cdexTranCodeValue === '' && revLineTypeCode === '');

    //                    newTransactionList.push({
    //                        ...transaction,
    //                        [fieldName]: value,
    //                        isDirty: isDirty,
    //                        isValid: isValid
    //                    });

    //                    if (isDirty && isValid)
    //                        anyTransactionDirty = true;

    //                    if (isDirty && !isValid)
    //                        anyTransactionInvalid = true;
    //                }
    //                else {
    //                    newTransactionList.push(transaction);

    //                    if (transaction.isDirty && transaction.isValid)
    //                        anyTransactionDirty = true;

    //                    if (transaction.isDirty && !transaction.isValid)
    //                        anyTransactionInvalid = true;
    //                }
    //            });

    //            newProductList.push({
    //                ...product,
    //                cdexTransactionRowDTO: { ...product.cdexTransactionRowDTO, data: newTransactionList }
    //            });

    //            //console.log('Updating transaction: ' + transaction.transactionCode);
    //        }
    //        else {
    //            newProductList.push(product);
    //        }
    //    });
    //    setProductCodes(newProductList);
    //    setSelectedProduct(newProductList[selectedProductIndex]);
    //    setAnyTransactionDirty(anyTransactionDirty && !anyTransactionInvalid);

    //    newProductList.map((product, productIndex) => {
    //        console.log('New Product: ' + product.productCode + ', ' + product.productDesc + ', ' + product.cdexProductCode + ', ' + product.isDirty);

    //        if (productIndex === selectedProductIndex) {
    //            product.cdexTransactionRowDTO.data.map(transaction => {
    //                console.log('New Transaction: ' + transaction.transactionCode + ', ' + transaction.transactionDesc + ', ' + transaction.cdexTransactionCode + ', ' + transaction.revLineTypeCode + ', ' + transaction.isDirty + ', ' + transaction.isValid);
    //            });
    //        }
    //    });
    //}

    //update products
    const updateClassifications = async () => {
        console.log('Updating Classifications...');
        setDataStatus('loading');

        //disable Map buttons while mapping
        //const tranDirtyState = anyTransactionDirty;
        //setAnyTransactionDirty(false);
        setAnyClassificationDirty(false);

        //console.log('Id: ' + selectedProduct.id + ', Product Code: ' + selectedProduct.productCode);

        await saveClassifications()
            .then(response => {
                if (response.success) {
                    setAnyClassificationDirty(false);
                    setOriginalClassificationCodes(response.data);
                    setClassificationCodes(response.data);
                    setSelectedClassification(response.data[selectedClassificationIndex]);
                    //setAnyTransactionDirty(tranDirtyState);
                    setDataStatus('ready')
                }
                else {
                    //TODO: figure out what to do here
                    console.log('nodata');
                    //setDataStatus('nodata')
                }
            })
            .catch(ex => {
                setDataStatus('error');
                setDataErrors(ex.message);
            })
            .finally(response => {
                //do something
            })
    };

    //save products
    const saveClassifications = async () => {
        //console.log('Saving Products...');

        const apiUrl = 'accounts/' + targetAccountId + '/updatemajoraccountcodes';
        const jsonBody = JSON.stringify(classificationCodes);
        //console.log(apiUrl);
        //console.log(jsonBody);

        const response = await apiPut(apiUrl, jsonBody, getAccessTokenSilently);
        const json = await response.json();

        return { success: response.ok, data: json };
    };

    //pencil toggle for a classification row — flips isReadOnly so the dropdown becomes editable
    const togglePencilClassification = (index) => {
        const newList = classificationCodes.map((c, i) =>
            i === index ? { ...c, isReadOnly: !c.isReadOnly } : c
        );
        setClassificationCodes(newList);
        if (index === selectedClassificationIndex) {
            setSelectedClassification(newList[index]);
        }
    };

    //update transaction
    //const updateTransactions = async () => {
    //    console.log('Updating Transactions...');
    //    setDataStatus('loading');

    //    //disable Map buttons while mapping
    //    const prodDirtyState = anyProductDirty;
    //    setAnyTransactionDirty(false);
    //    setAnyProductDirty(false);

    //    //console.log('Id: ' + selectedProduct.id + ', Product Code: ' + selectedProduct.productCode);

    //    await saveTransactions()
    //        .then(response => {
    //            if (response.success) {
    //                setAnyTransactionDirty(false);

    //                const newProductList = [];
    //                productCodes.map((product, index) => {
    //                    if (index === selectedProductIndex) {
    //                        newProductList.push(response.data);
    //                    }
    //                    else {
    //                        newProductList.push(product);
    //                    }
    //                });
    //                setOriginalProductCodes(newProductList);
    //                setProductCodes(newProductList);
    //                setSelectedProduct(newProductList[selectedProductIndex]);

    //                setAnyProductDirty(prodDirtyState);
    //                setDataStatus('ready')
    //            }
    //            else {
    //                //TODO: figure out what to do here
    //                console.log('nodata');
    //                //setDataStatus('nodata')
    //            }
    //        })
    //        .catch(ex => {
    //            setDataStatus('error');
    //            setDataErrors(ex.message);
    //        })
    //        .finally(response => {
    //            //do something
    //        })
    //};

    //save transactions
    //const saveTransactions = async () => {
    //    console.log('Saving Transactions...');

    //    const apiUrl = 'accounts/' + currentAccount.id + '/updatetransactioncodes';
    //    const jsonBody = JSON.stringify(selectedProduct);
    //    //console.log(apiUrl);
    //    //console.log(jsonBody);

    //    const response = await apiPut(apiUrl, jsonBody, getAccessTokenSilently);
    //    const json = await response.json();

    //    return { success: response.ok, data: json };
    //};

    //handle mouse over
    const handleMouseOver = async (index) => {
        console.log('Mouse Over: ' + index);

        if (index != selectedClassificationIndex) {
            setHoveredIndex(index);
        }
    };

    //handle mouse leave
    const handleMouseLeave = async (index) => {
        console.log('Mouse Leave: ' + index);

        if (index != selectedClassificationIndex) {
            setHoveredIndex(null);
        }
    };

    //handle mouse click
    const handleMouseClick = async (index) => {
        console.log('Mouse Click: ' + index);

        setHoveredIndex(null);
        setSelectedClassificationIndex(index);
        setSelectedClassification(classificationCodes[index]);

        //let anyTransactionDirty = false;
        //let anyTransactionInvalid = false;
        //productCodes[index].cdexTransactionRowDTO.data.map(transaction => {
        //    if (transaction.isDirty && transaction.isValid) {
        //        anyTransactionDirty = true;
        //    }

        //    if (transaction.isDirty && !transaction.isValid) {
        //        anyTransactionInvalid = true;
        //    }
        //});
        //setAnyTransactionDirty(anyTransactionDirty && !anyTransactionInvalid);
    };

    //handle page click
    const handlePageClick = async (currentPage) => {
        //console.log('Current Page: ' + currentPage);

        //setDataStatus('loading');

        setDataCriteria({
            ...dataCriteria,
            skipCount: 0
        });

        //don't need to do here, since pagination component sets before calling this method
        //setCurrentPage(1);

        await populateMajorAccountCodes(currentPage);
    };

    //render page loading
    //note: spinner overlay will eventually be at the page level (I think)
    const showPageLoading = () => {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                <Spinner animation="border" style={{ color: COLORS.gold }} />
            </div>
        );
    }

    //render page loading
    const showDataLoading = () => {
        return (
            <Row style={{ justifyContent: 'center', padding: '40px 0' }}>
                <Spinner animation="border" style={{ color: COLORS.gold, width: 'auto' }} />
            </Row>
        );
    }

    //render page error
    const showPageError = () => {
        return (
            <div>
                Oops, there was a page load error: {pageErrors}
            </div>
        );
    }

    //render data error
    const showDataError = () => {
        return (
            <Row>
                Oops, there was a data load error: {dataErrors}
            </Row>
        );
    }

    //render table container
    const showTableContainer = () => {
        return (
            <Card className="mb-4" style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 14px rgba(0,0,0,.10)' }}>
                <Card.Header style={{ background: '#333333', color: '#f1f1f1', borderBottom: 'none', padding: props.embedded ? '8px 12px' : '12px 16px' }}>
                    {showInputCriteria()}
                </Card.Header>
                <Card.Body style={{ paddingLeft: '12px', paddingRight: '10px' }} >
                    {(() => {
                        switch (dataStatus) {
                            case 'nodata':
                                return <p>No Data Loaded.</p>
                            case 'loading':
                                return showDataLoading()
                            case 'ready':
                                return showData()
                            case 'error':
                                return showDataError()
                            default:
                                return <Row>No Data Loaded.</Row>
                        }
                    })()}
                </Card.Body>
            </Card>
        );
    }

    //render productCodes
    const showData = () => {
        return (
            <Row>
                <Col xs={5} style={{ marginTop: '-16px', marginBottom: '-16px', overflow: 'auto', paddingRight: '0px', paddingLeft: '0px', borderRight: '1px solid #e5e7eb' }} >
                    {(() => {
                        switch (dataStatus) {
                            case 'ready':
                                return showDataMaster()
                            default:
                                return null
                        }
                    })()}
                </Col>
                {/*<Col xs={7} style={{ height: '530px', overflow: 'auto', paddingLeft: '20px', marginTop: '-10px', marginBottom: '-16px' }}>*/}
                {/*    {(() => {*/}
                {/*        switch (dataStatus) {*/}
                {/*            case 'ready':*/}
                {/*                return showDataDetail()*/}
                {/*            default:*/}
                {/*                return null*/}
                {/*        }*/}
                {/*    })()}*/}
                {/*</Col>*/}
            </Row>
        );
    }

    //render master pane (productCodes list)
    const showDataMaster = () => {
        return (
            <>
                <ul style={{ listStyleType: "none", height: props.embedded ? "390px" : "450px", marginRight: '12px' }}>
                    {classificationCodes.map((majorAccountCode, index) => (
                        <li key={index} ref={(index == 0 ? companyTopRef : null)}
                            style={{ cursor: 'pointer', paddingLeft: '20px', marginLeft: '-42px', marginRight: '0px', fontSize: props.embedded ? '.8rem' : '.9rem' }}
                            onMouseOver={() => handleMouseOver(index)}
                            onMouseLeave={() => handleMouseLeave(index)}
                            onClick={() => handleMouseClick(index)}>
                            <Row style={{
                                borderTop: hoveredIndex == index ? `2px solid ${COLORS.gold}` : '2px solid transparent',
                                borderBottom: hoveredIndex == index ? `2px solid ${COLORS.gold}` : '1px solid #e5e7eb',
                                backgroundColor: (selectedClassificationIndex == index ? '#f5f0e8' : (index % 2 === 0 ? '#fafbfc' : 'transparent')),
                                color: selectedClassificationIndex == index ? '#1f2937' : '#374151',
                                fontWeight: selectedClassificationIndex == index ? 600 : 400,
                                borderLeft: selectedClassificationIndex == index ? `5px solid ${COLORS.gold}` : '5px solid transparent',
                                paddingTop: '4px',
                                paddingBottom: '4px',
                                transition: 'border-color 120ms ease, background 120ms ease, color 120ms ease'
                            }} >
                                {/* Pencil icon — toggles isReadOnly so the row becomes editable */}
                                <Col xs={1} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingLeft: 0, paddingRight: 0 }}>
                                    {majorAccountCode.isReadOnly && (
                                        <FontAwesomeIcon
                                            icon={faPenToSquare}
                                            style={{ cursor: 'pointer', color: '#2563EB' }}
                                            onClick={(e) => { e.stopPropagation(); togglePencilClassification(index); }}
                                        />
                                    )}
                                </Col>
                                {/* Major Account Code column hidden for _dc */}
                                <Col xs={6} style={{ display: 'flex', alignItems: 'center' }}>
                                    <div>{majorAccountCode.majorAccountDesc}</div>
                                </Col>
                                <Col xs={5}>
                                    {(() => {
                                        if (!majorAccountCode.isReadOnly) {
                                            return <Form.Group className="mb-0" controlId="majorClassCode">
                                                <Form.Select name="majorClassCode" size="sm" aria-label="Major Class Code" value={majorAccountCode.majorClassCode} style={{ fontSize: '.85rem', padding: '0px 22px 0px 6px', height: '24px', lineHeight: 1.2, backgroundPosition: 'right 4px center' }} onChange={e => handleClassificationChange(e)}>
                                                    {classificationTypes.map((type, i) => (
                                                        <option key={i} value={type.code}>{type.value}</option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        }
                                        else {
                                            return <div>
                                                {majorAccountCode.majorClassCode}
                                            </div>
                                        }
                                    })()}
                                </Col>
                            </Row>
                        </li>
                    ))}
                </ul>
            </>
        );
    }

    //render detail pane (company display or edit)
    //const showDataDetail = () => {
    //    return (
    //        <>
    //            {(() => {
    //                if (isReadOnly) {
    //                    //return showDataDisplay()
    //                    //return showDataEdit()
    //                }
    //                else {
    //                    //return showDataEdit()
    //                    return showDataDisplay()
    //                }
    //            })()}
    //        </>
    //    );
    //}

    //render company detail
    //const showDataDisplay = () => {
    //    return (
    //        <>
    //            <Row>
    //                <Col xs={8}>
    //                    <div style={{ fontWeight: '600', fontSize: '1.25rem', marginTop: '10px' }} >Sender Detail</div>
    //                </Col>
    //                <Col xs={4} className="d-flex justify-content-end" style={{ marginTop: '13px' }} >
    //                    Company Code:&nbsp;&nbsp;{selectedProduct.companyCode}&nbsp;&nbsp;&nbsp;&nbsp;
    //                </Col>
    //            </Row>
    //            <Form style={{ borderTop: 'solid lightgray 1px', marginTop: '10px', paddingRight: '10px' }} />
    //            <Row style={{ paddingRight: '10px', paddingTop: '10px' }}>
    //                <Col xs={2}>
    //                    Name:
    //                </Col>
    //                <Col xs={10}>
    //                    <div>
    //                        {selectedProduct.companyName}
    //                    </div>
    //                    <div>
    //                        {((selectedProduct.additionalNameInfo ?? '') == '' ? null : '(' + selectedProduct.additionalNameInfo + ')')}
    //                    </div>
    //                </Col>
    //            </Row>
    //            <Row>
    //                <Col xs={2}>
    //                    Address:
    //                </Col>
    //                <Col xs={10}>
    //                    <div>
    //                        {selectedProduct.address1} {selectedProduct.address2} {selectedProduct.address3}
    //                    </div>
    //                    <div>
    //                        {selectedProduct.city}, {selectedProduct.state} {selectedProduct.zip}
    //                    </div>
    //                </Col>
    //            </Row>
    //            <Row>
    //                <Col xs={2}>
    //                    Email:
    //                </Col>
    //                <Col xs={10}>
    //                    <div style={{ color: ((selectedProduct.email ?? '') == '' ? 'red' : 'black') }}>{(selectedProduct.email ?? '') == '' ? 'Not available' : selectedProduct.email}</div>
    //                </Col>
    //            </Row>
    //            <Row>
    //                <Col xs={2}>
    //                    Phone:
    //                </Col>
    //                <Col xs={10}>
    //                    <div style={{ color: ((selectedProduct.phone ?? '') == '' ? 'red' : 'black') }}>{(selectedProduct.phone ?? '') == '' ? 'Not available' : selectedProduct.phone}</div>
    //                </Col>
    //            </Row>
    //            <Row>
    //                <Col xs={2}>
    //                    Fax:
    //                </Col>
    //                <Col xs={10}>
    //                    <div style={{ color: ((selectedProduct.fax ?? '') == '' ? 'red' : 'black') }}>{(selectedProduct.fax ?? '') == '' ? 'Not available' : selectedProduct.fax}</div>
    //                </Col>
    //            </Row>
    //            //<Row>
    //            //    <Col xs={2}>
    //            //        Tax Reg #:
    //            //    </Col>
    //            //    <Col xs={10}>
    //            //        <div style={{ color: ((selectedProduct.taxRegNumber ?? '') == '' ? 'red' : 'black') }}>{(selectedProduct.taxRegNumber ?? '') == '' ? 'Not available' : selectedProduct.taxRegNumber}</div>
    //            //    </Col>
    //            //</Row>
    //            <Row>
    //                <Col xs={3}>
    //                    CDEX Sender Code:
    //                </Col>
    //                <Col xs={9}>
    //                    <div style={{ color: ((selectedProduct.majorAccountSenderCode ?? '') == '' ? 'red' : 'black') }}>{(selectedProduct.majorAccountSenderCode ?? '') == '' ? 'Not available' : selectedProduct.cdexSenderCode}</div>
    //                </Col>
    //            </Row>
    //        </>
    //    );
    //}

    //render detail pane (cdexTransactionCodes list)
    //const showDataEdit = () => {
    //    return (
    //        <>
    //            <ul style={{ listStyleType: "none", height: "515px", marginRight: '12px' }}>
    //                {selectedProduct.cdexTransactionRowDTO.data.map((cdexTransactionCode, index) => (
    //                    <li key={index} ref={(index == 0 ? companyTopRef : null)}
    //                        style={{ paddingLeft: '20px', marginLeft: '-42px', marginRight: '0px', fontSize: '.9rem' }}>
    //                        {/*onMouseOver={() => handleMouseOver(index)}*/}
    //                        {/*onMouseLeave={() => handleMouseLeave(index)}*/}
    //                        {/*onClick={() => handleMouseClick(index)}>*/}
    //                        <Row style={{
    //                            borderBottom: 'solid lightgray 1px',
    //                            //backgroundColor: (hoveredIndex == index ? '#9AC5F4' : (selectedProductIndex == index ? '#4F709C' : 'white')),
    //                            //color: (hoveredIndex == index ? 'white' : (selectedProductIndex == index ? 'white' : 'black'))
    //                        }} >
    //                            <Col xs={3} >
    //                                <div>{cdexTransactionCode.transactionCode}</div>
    //                            </Col>
    //                            <Col xs={3} >
    //                                <div>{cdexTransactionCode.transactionDesc}</div>
    //                            </Col>
    //                            <Col xs={3} >
    //                                {(() => {
    //                                    if (!cdexTransactionCode.isReadOnly) {
    //                                        return <Form.Group className="mb-3" controlId="cdexTransactionCode">
    //                                            <Form.Control name="cdexTransactionCode" size="xs" aria-label="CDEX Transaction Code" value={cdexTransactionCode.cdexTransactionCode} style={{ width: '100' }} onChange={(e) => handleTransactionChange(index, e)} />
    //                                        </Form.Group>
    //                                    }
    //                                    else {
    //                                        return <div>{cdexTransactionCode.cdexTransactionCode}</div>;
    //                                    }
    //                                })()}
    //                            </Col>
    //                            <Col xs={3} >
    //                                {(() => {
    //                                    if (!cdexTransactionCode.isReadOnly) {
    //                                        return <Form.Group className="mb-3" controlId="companyCode">
    //                                            <Form.Select name="revLineTypeCode" size="sm" aria-label="REV Line Type" value={cdexTransactionCode.revLineTypeCode} onChange={(e) => handleTransactionChange(index, e)} >
    //                                                {revLineTypes.map((type, i) => (
    //                                                    <option key={i} value={type.code}>{type.value}</option>
    //                                                ))}
    //                                            </Form.Select>
    //                                        </Form.Group>
    //                                    }
    //                                    else {
    //                                        switch (cdexTransactionCode.revLineTypeCode) {
    //                                            case 'R':
    //                                                return <div>REV</div>
    //                                            case 'T':
    //                                                return <div>TAX</div>
    //                                            case 'D':
    //                                                return <div>DED</div>
    //                                            case 'N':
    //                                                return <div>NET</div>
    //                                            default:
    //                                                return <div></div>
    //                                        }
    //                                    }
    //                                })()}
    //                            </Col>
    //                        </Row>
    //                    </li>
    //                ))}
    //            </ul>
    //        </>
    //    );
    //}

    //pagination example
    //https://github.com/lukaaspl/ellipsis-pagination/blob/master/src/components/Pagination.js

    //render input criteria
    const showInputCriteria = () => {
        return (
            <Container fluid>
                <Row>
                    <Col xs={5}>
                        <Button
                            size='sm'
                            style={{ ...btnGold, minWidth: props.embedded ? '160px' : '200px', padding: props.embedded ? '2px 12px' : undefined, fontSize: props.embedded ? '.8rem' : undefined, whiteSpace: 'nowrap' }}
                            onMouseEnter={e => Object.assign(e.target.style, boxshadowHover)}
                            onMouseLeave={e => Object.assign(e.target.style, boxshadowUnHover(btnGold))}
                            disabled={!anyClassificationDirty}
                            onClick={anyClassificationDirty ? () => updateClassifications() : null}
                        >
                            Map Classification Codes
                        </Button>
                    </Col>
                </Row>
                <Row style={{ fontSize: props.embedded ? '.8rem' : '.9rem', marginTop: props.embedded ? '8px' : '20px', color: '#f1f1f1', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.04em' }} >
                    <Col xs={5}>
                        <Row>
                            <Col xs={1}></Col>
                            <Col xs={6}>Major Account Desc</Col>
                            <Col xs={5}>Major Class Code</Col>
                        </Row>
                    </Col>
                    {/*<Col xs={7} style={{ borderLeft: 'solid black 1px' }} >*/}
                    {/*    <Row>*/}
                    {/*        <Col xs={3}>Interest Type Code</Col>*/}
                    {/*        <Col xs={3}>Interest Type Desc</Col>*/}
                    {/*        <Col xs={3}>CDEX Transaction Code</Col>*/}
                    {/*        <Col xs={3}>REV Line Type</Col>*/}
                    {/*    </Row>*/}
                    {/*</Col>*/}
                </Row>

            </Container>
        );
    }

    return (
        <div>
            {!props.embedded && (
                <Row className="align-items-center" style={{ margin: '0 0 4px 0' }}>
                    <Col xs={1} style={{ marginLeft: '-7px' }}>
                        <span style={{ ...backBtn }} onClick={() => navigate('/settings')}>
                            <FontAwesomeIcon icon={faArrowLeft} />
                        </span>
                    </Col>
                    <Col xs={10}>
                        <p style={{ fontSize: '1.75rem', fontWeight: '700', color: '#333333', margin: 0, textAlign: 'center' }}>JIB Classification Override</p>
                    </Col>
                    <Col xs={1}></Col>
                </Row>
            )}
            {(() => {
                switch (pageStatus) {
                    case 'loading':
                        return showPageLoading()
                    case 'ready':
                        return showTableContainer()
                    case 'error':
                        return showPageError()
                    default:
                        return <p>No Page Loaded.</p>
                }
            })()}
        </div>
    );
}

export default ClassificationCodesOverride_dc;
